/* BRIEF 134 §1 — THE ANCHORED PORTAL MENU, EXTRACTED FROM `FieldSelect`.
 *
 * This is `FieldSelect.tsx:350-426@b7718bf5` — the resize dismissal and the
 * measured placement of a `position: fixed` menu under the field that opened
 * it. IT IS AN EXTRACTION AND NOTHING ELSE (rule 3): every line below was in
 * `FieldCombo`, and its behaviour there is unchanged by the move.
 *
 * WHY IT MOVED. §1 asks for a portalled `MatterPicker` menu and says, in its
 * own words, "the tree has `createPortal` precedent — census the existing
 * implementations and reuse the one that fits; do not write another."
 *
 * The census, run over `src/`:
 *
 *   FieldSelect.tsx:485      a MENU anchored to an INPUT, measured, flipped,
 *                            zoom-corrected, with a dismiss backdrop  ← this
 *   TimeField.tsx:249        a menu anchored to a button, rect snapshot in
 *                            state, no flip and no zoom correction
 *   ColorOrVarPicker.tsx:315 a panel anchored to a swatch, rect in state
 *   CaseTaskRow.tsx:535      a status menu anchored to a pill, rect in state
 *   BadgeHoverArea.tsx:83    a hover card, not a picker
 *   SideFlyPanel / CallsPanel / CallWindow / FolderPropagationModal /
 *   OutlookImportModal / App.tsx:14376 / :18057 / :18062
 *                            whole surfaces portalled to body or to a
 *                            pop-out window — no anchor at all
 *
 * Only the first one is the same problem: a list that must open under a text
 * input, inside a container that clips. It is also the only one that survived a
 * production defect and carries the correction — the rect-in-state snapshot the
 * other three still use is precisely what failed on 2026-08-03, and the reason
 * is written out at `place()` below. Reusing this one means `MatterPicker` gets
 * that correction rather than a fourth copy of the bug.
 */
import { useEffect, useLayoutEffect } from "react";
import type { RefObject } from "react";

export type AnchoredMenuOpts = {
  /** Whether the menu is mounted. Nothing is measured while it is false. */
  open: boolean;
  /** The control the menu belongs under — the element whose width it takes. */
  anchorRef: RefObject<HTMLElement | null>;
  /** The portalled menu itself. */
  menuRef: RefObject<HTMLElement | null>;
  /** Close it. Called on resize, and on a scroll that takes the anchor out of
   *  sight.
   *
   *  The two consumers spell "closed" differently — one has an `open` flag,
   *  the other a query sentinel — so the hook asks rather than assumes. */
  onDismiss: () => void;
  /** Extra values that change the menu's HEIGHT, appended to the effect's own
   *  deps. Each consumer passes a fixed-length literal, so the array's length
   *  is stable across renders. */
  deps?: unknown[];
  /** A floor on the menu's width, in CSS pixels. Default 0 — the menu is
   *  exactly as wide as its anchor, which is what `FieldSelect` has always had
   *  and is unchanged by this parameter's existence.
   *
   *  134 §1 adds it for `MatterPicker`, whose chat host is 130px wide by 132
   *  §1's layout: a menu that escapes its clip and then ellipsises every matter
   *  title to six characters has not been made visible in any sense a reader
   *  would accept. A portalled menu is no longer bound by its host's box, which
   *  is the whole point of portalling it. */
  minWidth?: number;
  /** 207B §7 — WIDENED (rule 4) for the time picker, whose panel — a clock face
   *  and two columns — is wider than the field it opens under and keeps ITS
   *  OWN width: nothing is written to it, and the horizontal clamp reads it.
   *  The scale is calibrated the way the default path calibrates it — a known
   *  width written, read back, then taken off again — so the zoom correction
   *  is the same one. Default false: the anchor's width, exactly as before. */
  ownWidth?: boolean;
};

/* IS THE ANCHOR STILL ON SCREEN — asked of the ancestors, not of the viewport
 * alone.
 *
 * It is the same walk §1's measurement ran to name `.scW`: an element can sit
 * perfectly inside the window and still be scrolled out of an inner container
 * that clips. Testing the viewport alone would leave the menu tracking a field
 * nobody can see, which is the stranding this exists to prevent, one layer in.
 *
 * `overflow: visible` on all three axes is the only thing that does not clip,
 * so anything else gets its box compared against the anchor's. */
/* 136 §1 — THE WINDOW IS THE ANCHOR'S, NOT THE MODULE'S.
 *
 * Every consumer until now lived in the main document, where `viewOf(el)` is
 * `window` and nothing below changes. The status pill does not: it renders in
 * the compact column, which App.tsx portals into a SEPARATE pop-out document
 * (`popOutCtr`, :18620 — a real second window, which is why :4607 mirrors the
 * theme onto it by hand). A menu measured against the main window's
 * `innerWidth`/`innerHeight` while sitting in a 300px pop-out clamps and flips
 * against dimensions nobody is looking at.
 *
 * SESSION_RULES rule 4: widen the primitive, never bend the behaviour. The
 * fallback keeps a detached anchor working exactly as it did. */
const viewOf = (el: HTMLElement | null): Window =>
  el?.ownerDocument?.defaultView ?? window;

/* 207 §17 [BLOCKING, ON PROD] — A FIXED BOX ESCAPES ITS ANCESTORS' CLIP, SO THE
 * WALK STOPS WHERE THE ANCHOR'S BOX DOES.
 *
 * THE DEFECT, MEASURED (scripts/drive-207.js --part=menu, the shadow, Chromium
 * AND WebKit, every text size — 0.923 · 1 · 1.077 · 1.231): the « Save into
 * subfolder » menu of the document generator, inside a collective case of 250
 * members with 14 subfolders, CLOSED ON THE FIRST SCROLL — a wheel over the
 * menu, or the row a hover brings into view — and the next click landed on the
 * member list underneath: Laurent's « the list disappears and the client list
 * wins ». The generator's `ModalLayout` is mounted INSIDE the documents
 * section's `.detail-card` (`overflow-x: auto`), and this walk read that card
 * as clipping the trigger — the card sat at 432–508 px, the trigger at 705–735
 * inside the modal — so every scroll event the window hears ran `onDismiss`.
 * But the modal's overlay is `position: fixed`: nothing ABOVE a fixed box clips
 * it, unless an ancestor becomes that box's containing block (a transform, a
 * filter, a backdrop filter, a perspective, a `contain` of layout or paint, a
 * `will-change` naming one of those). The hit test was never wrong — a click
 * that followed no scroll picked its row in both engines at every size; the
 * MENU was gone before the click arrived.
 *
 * So the walk checks each ancestor as before, and at a `position: fixed` one it
 * jumps to that box's containing block — the nearest ancestor that establishes
 * one — or, when there is none (the overlay's case: the viewport contains it),
 * it stops. Every consumer of this hook (MenuSelect, TypeaheadPicker,
 * FieldSelect's searchable arm, StatusPill, ColorOrVarPicker) is mounted in
 * modals, and all of them had the same close. */
type Styles = ReturnType<Window["getComputedStyle"]>;
interface Box { top: number; bottom: number; left: number; right: number }
const outside = (r: Box, b: Box) =>
  r.bottom <= b.top || r.top >= b.bottom || r.right <= b.left || r.left >= b.right;
const clips = (cs: Styles) =>
  !(cs.overflow === "visible" && cs.overflowX === "visible" && cs.overflowY === "visible");
const makesFixedContainingBlock = (cs: Styles) =>
  cs.transform !== "none" ||
  cs.filter !== "none" ||
  (cs.getPropertyValue("backdrop-filter") || "none") !== "none" ||
  (cs.getPropertyValue("-webkit-backdrop-filter") || "none") !== "none" ||
  cs.perspective !== "none" ||
  /\b(layout|paint|strict|content)\b/.test(cs.contain || "") ||
  /\b(transform|filter|perspective)\b/.test(cs.willChange || "");
/** One ancestor of the anchor, as the walk reads it. */
export interface ChainLink { clips: boolean; box: Box; fixed: boolean; makesFixedCB: boolean }

/** 207 §17 — THE WALK'S DECISION, over a measured chain (nearest ancestor first):
 *  hidden when a CLIPPING ancestor's box leaves the anchor's out, where a
 *  `position: fixed` link escapes every link above it up to the next one that
 *  establishes its containing block — none, and nothing above clips. Pure, so
 *  `recordings/207-menu-click` proves the rule with no browser. */
export function hiddenByChain(anchor: Box, viewport: Box, chain: readonly ChainLink[]): boolean {
  if (outside(anchor, viewport)) return true;
  let i = 0;
  while (i < chain.length) {
    const l = chain[i];
    if (l.clips && outside(anchor, l.box)) return true;
    if (!l.fixed) {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < chain.length && !chain[j].makesFixedCB) j++;
    if (j >= chain.length) return false;
    i = j; // the containing block is examined in full by the next turn
  }
  return false;
}

const anchorHidden = (a: HTMLElement) => {
  const view = viewOf(a);
  const root = a.ownerDocument.documentElement;
  const r = a.getBoundingClientRect();
  if (!r.width && !r.height) return true;
  const chain: ChainLink[] = [];
  for (let n = a.parentElement; n && n !== root; n = n.parentElement) {
    const cs = view.getComputedStyle(n);
    chain.push({ clips: clips(cs), box: n.getBoundingClientRect(), fixed: cs.position === "fixed", makesFixedCB: makesFixedContainingBlock(cs) });
  }
  return hiddenByChain(r, { top: 0, left: 0, bottom: view.innerHeight, right: view.innerWidth }, chain);
};

/* 207B §7 (extract, rule 3) — THE PLACEMENT ARITHMETIC, PURE: `place()`'s own
   lines (below, as they stood), moved into a function of what `place()`
   MEASURES — the anchor's box and the view in screen space, the scale `z`
   between the space written in and the space read in, the menu's width and
   height in screen space — so a recording can replay a measured case through
   the code that places the menu. Its answer is in the WRITTEN space (divided
   by `z`), which `place()` writes as it did. Nothing else changes. */
export interface AnchorBox { top: number; bottom: number; left: number }
export function anchoredPlacement(
  a: AnchorBox,
  view: { innerWidth: number; innerHeight: number },
  z: number,
  menuW: number,
  menuH: number,
): { left: number; top: number; flip: boolean } {
  const gap = 2;
  const roomBelow = view.innerHeight - a.bottom;
  const flip = roomBelow < menuH + gap && a.top > menuH + gap;
  const top = flip ? a.top - menuH - gap : a.bottom + gap;
  const left = Math.max(4, Math.min(a.left, view.innerWidth - menuW - 4));
  return { left: left / z, top: top / z, flip };
}

export function useAnchoredMenu({
  open,
  anchorRef,
  menuRef,
  onDismiss,
  deps = [],
  minWidth = 0,
  ownWidth = false,
}: AnchoredMenuOpts) {
  useEffect(() => {
    if (!open) return;
    const view = viewOf(anchorRef.current);
    const onResize = () => onDismiss();
    view.addEventListener("resize", onResize);
    return () => view.removeEventListener("resize", onResize);
  }, [open]);

  // ── POSITIONING, and why it is measured rather than computed ───────────────
  //
  // THE DEFECT (prod, 2026-08-03, Laurent's glance): the menu rendered at the
  // BOTTOM-LEFT of the modal instead of under its field — in a client modal and
  // in the opposing-counsel one. Bottom-left of the page is precisely where a
  // `position: fixed` box lands when its `top`/`left` do not take effect: it
  // falls back to its static position, which for the last child of <body> is
  // below all content, at the left content edge.
  //
  // The old code took a `getBoundingClientRect()` SNAPSHOT into state at open
  // time and handed the numbers to React as inline styles. Two ways that goes
  // wrong, and this box has both:
  //
  //   · A rect captured before the modal is laid out is all zeros, and a zero
  //     rect is truthy, so the menu renders anyway — at the origin.
  //   · `document.documentElement.style.zoom` is how this app scales the whole
  //     UI (App.tsx, "zoom-based whole-app scaling"). A fixed child of a zoomed
  //     root expresses `top`/`left` in the ZOOMED space while
  //     getBoundingClientRect() reports SCREEN space, so the two disagree by the
  //     zoom factor — silently, and only for users not at the default size.
  //
  // So the position is MEASURED, in a layout effect, before paint:
  //   1. write the anchor's own width, then read the menu back;
  //   2. `landed.width / anchor.width` IS the scale factor between the space we
  //      are writing in and the space we are reading in — whatever causes it —
  //      so divide the coordinates by it. At zoom 1 this is a division by 1.
  //   3. clamp horizontally to the viewport, and flip ABOVE the field when there
  //      is no room below, so a field near the bottom of a tall modal stops
  //      opening a 260px menu off-screen.
  //
  // Self-calibrating beats a formula here: it corrects for the zoom this app
  // sets today and for any future containing-block change (a `transform` or
  // `contain` on an ancestor does the same thing) without anyone remembering
  // this comment.
  useLayoutEffect(() => {
    if (!open) return;
    const el = menuRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;

    const view = viewOf(anchor);
    let raf = 0;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      // Not laid out yet (a modal still mounting reports all zeros). Stay hidden
      // and try again next frame rather than rendering at the origin.
      if (!a.width && !a.height) { raf = requestAnimationFrame(place); return; }

      // `minWidth` defaults to 0, so `w` is `a.width` and every line below is
      // the arithmetic that was here before it existed.
      // 207B §7 — `ownWidth`: calibrate on a written 100 px, take it off again,
      // and let the menu's own width stand (and clamp by it).
      const w = ownWidth ? 100 : Math.max(a.width, minWidth);
      el.style.width = `${w}px`;
      const landed = el.getBoundingClientRect();
      const scale = landed.width > 0 ? landed.width / w : 1;
      const z = scale > 0 ? scale : 1;
      if (ownWidth) el.style.width = "";
      const menuW = ownWidth ? el.getBoundingClientRect().width : w;

      const at = anchoredPlacement(a, view, z, menuW, el.offsetHeight * z);

      if (!ownWidth) el.style.width = `${w / z}px`;
      el.style.left = `${at.left}px`;
      el.style.top = `${at.top}px`;
      el.style.visibility = "visible";
    };
    /* ── 134 §1 — AND IT TRACKS THE FIELD ON SCROLL ────────────────────────
       §1: "a portalled menu tracks its input's position on scroll and resize
       and closes on both. Say how." Here is how, and one half is a deliberate
       disagreement, recorded rather than worked around.

       RESIZE CLOSES, unchanged: a resize reflows the whole page and the field
       may not exist in the same form afterwards.

       SCROLL RE-PLACES, and closes only once the field is out of sight.
       Closing on every scroll event is the trap CLAUDE.md names in this exact
       context — "a capture-phase scroll listener catches events from any
       descendant, including programmatic scroll restores, which close the menu
       prematurely" — and this tree really does restore scroll programmatically
       (`keepScroll`, App.tsx, on status changes, dashboard filter toggles and
       timer state). A menu that shuts because something else put a container
       back where it was is a worse defect than the one §1 is fixing.

       Re-placing is strictly stronger than closing anyway: the menu is never
       left stranded, because it never stops being under its input. What close
       protects against — a menu hanging in space over a field that has gone —
       is what `anchorHidden` answers, against the clipping ancestors and not
       merely the viewport.

       Capture phase on `window`, because scroll does not bubble but does
       capture, and the scroller here is an ancestor (`.scW`, `.sidebar-scroll`,
       `.modal-body`) rather than the document. Passive, and coalesced to one
       measurement per frame. */
    let tick = 0;
    const onScroll = (e: Event) => {
      /* 207 §17 — THE MENU'S OWN SCROLL IS NOT A MOVE OF THE FIELD. Scrolling
         the list to reach its 13th row cannot take the anchor anywhere, so it
         neither re-places nor dismisses. */
      const tg = e.target;
      if (tg instanceof Node && menuRef.current?.contains(tg)) return;
      if (tick) return;
      tick = requestAnimationFrame(() => {
        tick = 0;
        const at = anchorRef.current;
        if (!at) return;
        if (anchorHidden(at)) onDismiss();
        else place();
      });
    };
    view.addEventListener("scroll", onScroll, { capture: true, passive: true });

    place();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (tick) cancelAnimationFrame(tick);
      view.removeEventListener("scroll", onScroll, { capture: true });
    };
    // Re-placed when the filtered list changes height — a menu that shrinks from
    // 249 rows to 3 while flipped above the field would otherwise float.
  }, [open, ...deps]);
}

export default useAnchoredMenu;
